import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../api/ApiProvider";
import useStore from "../helpers/useStore";
import { WebSocketContext } from "../stores/WebSocket/WebSocket.provider";
import { AgentStreamEvent } from "../stores/WebSocket/types";

/**
 * Represents a single message in the chat conversation.
 *
 * - "user" messages are what the staff member typed.
 * - "agent" messages are the AI agent's responses, which arrive
 *   token-by-token via WebSocket streaming and are assembled here.
 */
export interface ChatMessage {
  role: "user" | "agent";
  text: string;
}

interface UseAgentChatReturn {
  sendMessage: (agentName: string, message: string) => Promise<void>;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  threadId: string | null;
  clearChat: () => void;
}

/**
 * useAgentChat — Core hook for the AI Agent chat feature.
 *
 * ## Architecture: Hybrid REST + WebSocket
 *
 * This hook implements a two-phase communication pattern:
 *
 * 1. **REST (POST /ai-agents/chat)** — Initiates the agent job on the backend.
 *    Returns a `threadId` that uniquely identifies this conversation.
 *    The backend enqueues the job in BullMQ and returns immediately — the REST
 *    call does NOT wait for the agent to finish (non-blocking).
 *
 * 2. **WebSocket (Socket.IO)** — Receives the agent's response in real-time.
 *    After the REST call returns a threadId, this hook subscribes to the
 *    WebSocket room `agent:thread:{threadId}`. The backend streams the agent's
 *    response token-by-token as `delta` events via Redis Pub/Sub → WebSocket.
 *
 * ## Why this pattern?
 *
 * - REST alone would require long-polling or a very long HTTP timeout (agents
 *   can take 10-30 seconds). Not practical for streaming text.
 * - WebSocket alone would lose the reliability of a proper HTTP request/response
 *   cycle (status codes, error handling, authentication).
 * - The hybrid approach gives us: reliable job initiation (REST) + real-time
 *   token streaming (WebSocket). Best of both worlds.
 *
 * ## State Management
 *
 * All chat state lives locally in this hook (React useState), NOT in a MobX store.
 * The conversation history is persisted server-side in Redis (with sliding TTL),
 * so the backend maintains multi-turn context even if the user refreshes the page.
 * The frontend only needs to display the current session's messages.
 *
 * ## threadId — The Conversation Identifier
 *
 * The `threadId` ties everything together across the full stack:
 * - Frontend: used to subscribe/unsubscribe from the WebSocket room
 * - Backend REST: used to route the message to the correct BullMQ job
 * - Backend Worker: used as the Redis session key (`agent:session:{threadId}`)
 * - WebSocket Gateway: used as the room name (`agent:thread:{threadId}`)
 *
 * On the first message, threadId is null → the backend generates a new UUID.
 * On subsequent messages, we send the existing threadId → the backend appends
 * to the same conversation (multi-turn with session memory).
 */
export const useAgentChat = (): UseAgentChatReturn => {
  const api = useApi();
  const wsStore = useStore(WebSocketContext);

  /** The chat message history displayed in the UI */
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  /** True while the agent is generating a response (tokens are still arriving) */
  const [isStreaming, setIsStreaming] = useState(false);

  /** Holds the last error message, or null if no error */
  const [error, setError] = useState<string | null>(null);

  /**
   * The conversation thread ID, returned by the backend on the first message.
   * Stored in state so it's included in subsequent sendMessage calls (multi-turn).
   */
  const [threadId, setThreadId] = useState<string | null>(null);

  /**
   * Ref (not state) to track the currently active threadId for cleanup purposes.
   *
   * Why a ref instead of using the `threadId` state?
   * - The cleanup function in useEffect captures the ref's `.current` value at
   *   execution time, not at render time. This avoids stale closure issues where
   *   the cleanup would unsubscribe from an outdated threadId.
   * - State updates are async (batched by React), but we need the threadId
   *   immediately when unsubscribing in sendMessage and clearChat.
   */
  const activeThreadId = useRef<string | null>(null);

  /**
   * Cleanup on unmount: unsubscribe from the active WebSocket room.
   *
   * Without this, the WebSocket connection would keep receiving events for a
   * thread that no longer has a UI to display them — wasting bandwidth and
   * potentially causing memory leaks in the WebSocket gateway.
   */
  useEffect(() => {
    return () => {
      if (activeThreadId.current) {
        wsStore.unsubscribe("unsubscribeFromAgent", `agent:${activeThreadId.current}`, {
          threadId: activeThreadId.current,
        });
      }
    };
  }, [wsStore]);

  /**
   * WebSocket event listener for agent stream events.
   *
   * The backend publishes three event types to the `agentStream` channel:
   *
   * - `delta`: A small chunk of text (a few tokens) from the agent's response.
   *   We append it to the last message in the array (which is always the
   *   agent's placeholder message created in sendMessage). This gives the user
   *   the "typing" effect — text appears progressively as the agent thinks.
   *
   * - `done`: The agent has finished generating its response. We stop the
   *   streaming indicator so the user knows the answer is complete.
   *
   * - `error`: Something went wrong (e.g., agent timeout, OpenAI API error).
   *   We display the error and stop streaming.
   *
   * This effect registers the handler once and cleans it up on unmount.
   * The handler is stable (no dependencies change) so it won't re-register.
   */
  useEffect(() => {
    const handler = (event: AgentStreamEvent) => {
      switch (event.type) {
        case "delta":
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "agent") {
              updated[updated.length - 1] = {
                ...last,
                text: last.text + event.delta,
              };
            }
            return updated;
          });
          break;
        case "done":
          setIsStreaming(false);
          break;
        case "error":
          setError(event.error);
          setIsStreaming(false);
          break;
      }
    };

    wsStore.on("agentStream", handler);
    return () => {
      wsStore.off("agentStream", handler);
    };
  }, [wsStore]);

  /**
   * Sends a message to the AI agent and starts streaming the response.
   *
   * Step-by-step flow:
   * 1. Clear any previous error, set streaming to true
   * 2. Append the user's message + an empty agent placeholder to the messages array.
   *    The placeholder is where delta events will accumulate the response text.
   * 3. Unsubscribe from the previous WebSocket room (if switching threads or retrying)
   * 4. Call the REST API to start the agent job (POST /ai-agents/chat)
   *    - First message: threadId is undefined → backend creates a new thread
   *    - Subsequent messages: threadId is sent → backend continues the conversation
   * 5. Subscribe to the new WebSocket room to receive streaming events
   *
   * The REST call returns immediately (job is queued in BullMQ). The actual agent
   * processing happens asynchronously in a worker process. Results arrive via WebSocket.
   */
  const sendMessage = useCallback(
    async (agentName: string, message: string) => {
      setError(null);
      setIsStreaming(true);

      setMessages((prev) => [
        ...prev,
        { role: "user", text: message },
        { role: "agent", text: "" },
      ]);

      if (activeThreadId.current) {
        wsStore.unsubscribe("unsubscribeFromAgent", `agent:${activeThreadId.current}`, {
          threadId: activeThreadId.current,
        });
      }

      try {
        const response = await api.aiAgents.startAgentChat({
          agentName,
          message,
          threadId: threadId ?? undefined,
        });

        const newThreadId = response.threadId;
        setThreadId(newThreadId);
        activeThreadId.current = newThreadId;

        wsStore.subscribe("subscribeToAgent", `agent:${newThreadId}`, {
          threadId: newThreadId,
        });
      } catch (err: any) {
        setError(err.message || "Failed to start agent chat");
        setIsStreaming(false);
      }
    },
    [api.aiAgents, wsStore, threadId],
  );

  /**
   * Resets the entire chat state — clears messages, threadId, and unsubscribes
   * from the active WebSocket room.
   *
   * After clearing, the next sendMessage will create a fresh thread (no threadId
   * sent to the backend), starting a new conversation with no session history.
   * The old Redis session will auto-expire via its sliding TTL.
   */
  const clearChat = useCallback(() => {
    if (activeThreadId.current) {
      wsStore.unsubscribe("unsubscribeFromAgent", `agent:${activeThreadId.current}`, {
        threadId: activeThreadId.current,
      });
    }
    setMessages([]);
    setThreadId(null);
    activeThreadId.current = null;
    setError(null);
    setIsStreaming(false);
  }, [wsStore]);

  return { sendMessage, messages, isStreaming, error, threadId, clearChat };
};
